import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  resolveAccessorialsStatus,
  updateAccessorialException,
} from 'utils/AccessorialRequestWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import { humanize } from 'underscore.string';
import { isVendorChargePaid } from 'common/helpers/accessorials';
import {
  buildUpdatePayload,
  buildResolutionPayload,
} from '../accessorials/accessorialRequestPaneUtils';

export default function useAccessorialMutations({
  accessorial,
  accessorialId,
  formFields,
  fullShipment,
  onSave,
  refresh,
}) {
  // Edit mutation
  const editMutation = useMutation({
    mutationFn: (updates) => updateAccessorialException(updates),
    onSuccess: (res) => {
      if (res.entity?.success) {
        showToast('Success', 'Accessorial updated successfully', 'success');
        refresh?.();
        onSave?.();
      } else {
        showToast('Error', 'Failed to update accessorial', 'error');
      }
    },
    onError: (e) => {
      console.error('Failed to update accessorial:', e);
      showToast('Error', e.message || 'Failed to update accessorial', 'error');
    },
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (payload) => resolveAccessorialsStatus(payload),
    onSuccess: (res) => {
      const result = res?.entity?.result || {};
      const errors = result.errors || [];
      const successes = result.successes || [];
      if (errors.length && !successes.length) {
        // 200 but nothing was approved (e.g. accessorial not configured for the
        // shipper). Surface the specific reason and do NOT imply success.
        showToast('Approval failed', humanize(String(errors[0].error)), 'error');
        return;
      }
      if (errors.length) {
        showToast('Partial approval', humanize(String(errors[0].error)), 'warning');
      } else {
        showToast('Success', 'Accessorial approved successfully', 'success');
      }
      refresh?.();
      onSave?.();
    },
    onError: (e) => {
      console.error('Failed to approve accessorial:', e);
      showToast('Error', e.message || 'Failed to approve accessorial', 'error');
    },
  });

  // Decline mutation
  const declineMutation = useMutation({
    mutationFn: (payload) => resolveAccessorialsStatus(payload),
    onSuccess: (res) => {
      const result = res?.entity?.result || {};
      const errors = result.errors || [];
      const successes = result.successes || [];
      if (errors.length && !successes.length) {
        // 200 but nothing was declined. Surface the specific reason; no success.
        showToast('Decline failed', humanize(String(errors[0].error)), 'error');
        return;
      }
      if (errors.length) {
        showToast('Partial decline', humanize(String(errors[0].error)), 'warning');
      } else {
        showToast('Success', 'Accessorial declined successfully', 'success');
      }
      refresh?.();
      onSave?.();
    },
    onError: (e) => {
      console.error('Failed to decline accessorial:', e);
      showToast('Error', e.message || 'Failed to decline accessorial', 'error');
    },
  });

  // Extract stable .mutate refs for useCallback deps
  const { mutate: editMutate } = editMutation;
  const { mutate: approveMutate } = approveMutation;
  const { mutate: declineMutate } = declineMutation;

  const handleSaveButton = useCallback(
    (type) => {
      const updated = buildUpdatePayload(formFields, accessorial, type);
      if (updated) {
        editMutate([updated]);
      }
    },
    [formFields, accessorial, editMutate],
  );

  const handleRequestAction = useCallback(
    (type, action, declineNote, declineReasonCode) => {
      const isVendorPaid =
        accessorial?.carrier_status === 'approved' &&
        isVendorChargePaid(accessorialId, fullShipment?.vendor_payables);

      const { payload, validationError } = buildResolutionPayload(accessorial, {
        action,
        type,
        declineNote,
        declineReasonCode,
        isVendorPaid,
        isBulk: false,
        shipmentId: fullShipment?._id,
      });

      if (validationError) return;

      if (action === 'approve') {
        approveMutate(payload);
      } else {
        declineMutate(payload);
      }
    },
    [accessorial, accessorialId, fullShipment, approveMutate, declineMutate],
  );

  const handleConfirmBulk = useCallback(
    (bulkEvent, declineNote, declineReasonCode) => {
      const action = bulkEvent === 'approve-all' ? 'approve' : 'decline';

      const isVendorPaid =
        accessorial?.carrier_status === 'approved' &&
        isVendorChargePaid(accessorialId, fullShipment?.vendor_payables);

      const { payload, validationError } = buildResolutionPayload(accessorial, {
        action,
        declineNote,
        declineReasonCode,
        isVendorPaid,
        isBulk: true,
        shipmentId: fullShipment?._id,
      });

      if (validationError) return;

      if (action === 'approve') {
        approveMutate(payload);
      } else {
        declineMutate(payload);
      }
    },
    [accessorial, accessorialId, fullShipment, approveMutate, declineMutate],
  );

  const handleEquipmentSelect = useCallback(
    (selected, equipmentId) => {
      if (selected) return;
      editMutate([{ _id: formFields._id, equipment: [equipmentId] }]);
    },
    [formFields._id, editMutate],
  );

  return {
    isEditSaving: editMutation.isLoading,
    isApproveSaving: approveMutation.isLoading,
    isDeclineSaving: declineMutation.isLoading,
    handleSaveButton,
    handleRequestAction,
    handleConfirmBulk,
    handleEquipmentSelect,
  };
}
